'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

const BRAND = '#3757FF';

export default function Header() {
    const pathname = usePathname();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const navLinks = [
        { label: 'Home', href: '/' },
        { label: 'Wisly Learn', href: '/learn' },
        { label: 'Wisly Studio', href: '/studio' },
    ];

    return (
        <>
            <nav
                className="fixed top-0 left-0 w-full z-50 transition-all duration-500"
                style={{ paddingTop: scrolled ? '12px' : '20px', paddingBottom: scrolled ? '12px' : '20px' }}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div
                        className="relative flex items-center justify-between rounded-2xl px-5 py-3 transition-all duration-500"
                        style={scrolled ? {
                            background: 'rgba(255,255,255,0.92)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            boxShadow: '0 4px 32px rgba(55,87,255,0.08)',
                            border: '1px solid rgba(55,87,255,0.1)',
                        } : {}}
                    >
                        {/* Logo */}
                        <Link href="/wisly" className="flex items-center gap-1.5">

                            <AppLogo size={35} />

                            <span className="font-bold text-base text-foreground font-800" style={{ color: '#0D0D0D' }}>
                                Wisly
                            </span>
                        </Link>

                        {/* Desktop Nav */}
                        <div
                            className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-1 rounded-full px-2 py-1.5"
                            style={{
                                background: 'rgba(55,87,255,0.06)',
                                border: '1px solid rgba(55,87,255,0.1)'
                            }}
                        >
                            {navLinks.map((link) => {
                                const isActive =
                                    link.href === '/'
                                        ? pathname === '/'
                                        : pathname === link.href || pathname.startsWith(`${link.href}/`);

                                return (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        className="px-5 py-2 rounded-full text-xs font-700 uppercase tracking-widest transition-all duration-200"
                                        style={{
                                            color: isActive ? '#FFFFFF' : '#555',
                                            background: isActive ? BRAND : 'transparent',
                                            letterSpacing: '0.12em',
                                        }}
                                        onMouseEnter={e => {
                                            if (!isActive) {
                                                (e.currentTarget as HTMLElement).style.color = BRAND;
                                                (e.currentTarget as HTMLElement).style.background = 'rgba(55,87,255,0.08)';
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            if (!isActive) {
                                                (e.currentTarget as HTMLElement).style.color = '#555';
                                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                                            }
                                        }}
                                    >
                                        {link.label}
                                    </Link>
                                );
                            })}
                        </div>

                        {/* CTA */}
                        <div className="hidden md:flex items-center gap-3">
                            <a
                                href="/early-access"
                                className="text-sm font-600 px-5 py-2.5 rounded-xl transition-all duration-200"
                                style={{ color: BRAND, border: `1.5px solid ${BRAND}` }}
                                onMouseEnter={e => {
                                    (e.currentTarget as HTMLElement).style.background = BRAND;
                                    (e.currentTarget as HTMLElement).style.color = 'white';
                                }}
                                onMouseLeave={e => {
                                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                                    (e.currentTarget as HTMLElement).style.color = BRAND;
                                }}
                            >
                                Request Early Access
                            </a>
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="md:hidden p-2.5 rounded-xl"
                            style={{ background: 'rgba(55,87,255,0.08)', color: '#0D0D0D' }}
                            aria-label="Open menu"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            {mobileOpen && (
                <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(20px)' }}>
                    <div className="flex items-center justify-between px-6 py-5">
                        <Link href="/wisly" className="flex items-center gap-2.5" onClick={() => setMobileOpen(false)}>
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: BRAND }}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 4l5 8 5-8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <span className="font-display font-800 text-xl tracking-tight" style={{ color: '#0D0D0D' }}>Wisly</span>
                        </Link>
                        <button onClick={() => setMobileOpen(false)} className="p-2.5 rounded-xl" style={{ background: 'rgba(55,87,255,0.08)' }} aria-label="Close menu">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M5 5l10 10M15 5L5 15" stroke="#0D0D0D" strokeWidth="1.75" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                    <div className="flex flex-col gap-2 px-6 mt-8">
                        {navLinks.map((link) => {
                            const isActive =
                                link.href === '/'
                                    ? pathname === '/'
                                    : pathname === link.href || pathname.startsWith(`${link.href}/`);

                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setMobileOpen(false)}
                                    className="text-2xl font-700 tracking-tight py-4 transition-colors"
                                    style={{
                                        color: isActive ? BRAND : '#0D0D0D',
                                        borderBottom: '1px solid rgba(55,87,255,0.1)',
                                    }}
                                >
                                    {link.label}
                                </Link>
                            );
                        })}
                    </div>
                    <div className="px-6 mt-8">
                        <a
                            href="/early-access"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center justify-center w-full py-4 rounded-xl text-base font-700 text-white transition-all"
                            style={{ background: BRAND }}
                        >
                            Request Early Access
                        </a>
                    </div>
                </div>
            )}
        </>
    );
}
